import { registerPageInit } from '../barba/page-init-registry';
import '../../css/pages/about/about-construction.scss';
import Swiper from 'swiper';
import type { SwiperOptions } from 'swiper/types';
import { Autoplay, Navigation, EffectFade } from 'swiper/modules';
import 'swiper/css';

class AboutConstruction {
  private readonly root: HTMLElement | null;
  private mainSwiper: Swiper | null = null;
  private timelineSwiper: Swiper | null = null;
  private underSliderMounts: Array<{
    swipers: Map<number, Swiper>;
    clusterButtons: HTMLButtonElement[];
    panelEls: HTMLElement[];
    prevButton: HTMLButtonElement;
    nextButton: HTMLButtonElement;
  }> = [];
  private isSyncing = false;

  private static readonly UNDER_CONSTRUCTION_BLOCK_SELECTORS = [
    {
      root: '.js-under-construction',
      panel: '.js-under-construction__panel',
      swiper: '.js-under-construction__swiper',
      clusterButton: '.js-under-construction__cluster-button',
      prev: '.js-under-construction__button--prev',
      next: '.js-under-construction__button--next',
    },
    {
      root: '.js-construction-history',
      panel: '.js-construction-history__panel',
      swiper: '.js-construction-history__swiper',
      clusterButton: '.js-construction-history__cluster-button',
      prev: '.js-construction-history__button--prev',
      next: '.js-construction-history__button--next',
    },
  ] as const;

  constructor() {
    this.root = document.querySelector<HTMLElement>('.js-construction-hero');
  }

  public init(): void {
    this.destroy();

    this.initUnderConstructionSwipers();

    if (!this.root) return;

    const mainEl = this.root.querySelector<HTMLElement>('.js-construction-hero__main-swiper');
    const timelineEl = this.root.querySelector<HTMLElement>(
      '.js-construction-hero__timeline-swiper',
    );
    const prevEl = this.root.querySelector<HTMLElement>('.js-construction-hero__button--prev');
    const nextEl = this.root.querySelector<HTMLElement>('.js-construction-hero__button--next');

    if (!mainEl || !timelineEl || !prevEl || !nextEl) return;

    const autoplayOptions: SwiperOptions['autoplay'] = {
      delay: 6000,
      disableOnInteraction: false,
      pauseOnMouseEnter: true,
    };

    this.mainSwiper = new Swiper(mainEl, {
      modules: [Autoplay, Navigation, EffectFade],
      effect: 'fade',
      fadeEffect: {
        crossFade: true,
      },
      simulateTouch: false,
      slidesPerView: 1,
      speed: 700,
      rewind: true,
      autoplay: autoplayOptions,
      navigation: {
        prevEl,
        nextEl,
      },
    });

    this.timelineSwiper = new Swiper(timelineEl, {
      modules: [],
      slidesPerView: 2.2,
      slidesPerGroup: 1,
      spaceBetween: 20,
      speed: 700,
      rewind: true,
      slideToClickedSlide: true,
      watchSlidesProgress: true,
      breakpoints: {
        576: {
          slidesPerView: 3,
          spaceBetween: 20,
        },
        992: {
          slidesPerView: 4,
          spaceBetween: 24,
        },
        1200: {
          slidesPerView: 5,
          spaceBetween: 28,
        },
      },
    });

    const updateTimelineMarker = (index: number): void => {
      if (!this.timelineSwiper) return;

      this.timelineSwiper.slides.forEach((slide, slideIndex) => {
        slide.classList.toggle('construction-hero__timeline-slide--current', slideIndex === index);
      });
    };

    updateTimelineMarker(this.mainSwiper.activeIndex);

    this.mainSwiper.on('slideChange', () => {
      if (!this.mainSwiper || !this.timelineSwiper || this.isSyncing) return;

      this.isSyncing = true;
      const activeIndex = this.mainSwiper.activeIndex;
      this.timelineSwiper.slideTo(activeIndex);
      updateTimelineMarker(activeIndex);
      this.isSyncing = false;
    });

    this.timelineSwiper.on('click', () => {
      if (!this.mainSwiper || !this.timelineSwiper || this.isSyncing) return;

      const clickedIndex = this.timelineSwiper.clickedIndex;
      if (clickedIndex === undefined || clickedIndex < 0) return;
      this.isSyncing = true;
      this.mainSwiper.slideTo(clickedIndex);
      this.timelineSwiper.slideTo(clickedIndex);
      updateTimelineMarker(clickedIndex);
      this.isSyncing = false;
    });
  }

  private initUnderConstructionSwipers(): void {
    AboutConstruction.UNDER_CONSTRUCTION_BLOCK_SELECTORS.forEach((selectors) => {
      const underRoot = document.querySelector<HTMLElement>(selectors.root);
      if (!underRoot) return;

      const prevEl = underRoot.querySelector<HTMLButtonElement>(selectors.prev);
      const nextEl = underRoot.querySelector<HTMLButtonElement>(selectors.next);
      const panelEls = Array.from(underRoot.querySelectorAll<HTMLElement>(selectors.panel));

      if (!prevEl || !nextEl || panelEls.length === 0) return;

      const clusterButtons = Array.from(
        underRoot.querySelectorAll<HTMLButtonElement>(selectors.clusterButton),
      );

      const swipers = new Map<number, Swiper>();
      let activeClusterIndex = 0;

      const initClusterSwiper = (clusterIndex: number): Swiper | null => {
        const existingSwiper = swipers.get(clusterIndex);
        if (existingSwiper) return existingSwiper;

        const panelEl = panelEls[clusterIndex];
        const swiperEl = panelEl?.querySelector<HTMLElement>(selectors.swiper);
        if (!swiperEl) return null;

        const swiper = new Swiper(swiperEl, {
          modules: [Autoplay],
          slidesPerView: 1.2,
          slidesPerGroup: 1,
          centeredSlides: true,
          spaceBetween: 16,
          speed: 500,
          rewind: true,
          watchOverflow: true,
          breakpoints: {
            576: {
              slidesPerView: 2,
              spaceBetween: 16,
              centeredSlides: false,
            },
            992: {
              slidesPerView: 3,
              spaceBetween: 20,
              centeredSlides: false,
            },
            1200: {
              slidesPerView: 3,
              spaceBetween: 24,
              centeredSlides: false,
            },
          },
        });

        swipers.set(clusterIndex, swiper);
        return swiper;
      };

      const setActiveCluster = (clusterIndex: number): void => {
        const lastClusterIndex = Math.max(panelEls.length - 1, 0);
        const nextClusterIndex = Math.min(Math.max(clusterIndex, 0), lastClusterIndex);

        panelEls.forEach((panelEl, panelIndex) => {
          panelEl.classList.toggle(
            'under-construction__swiper-panel--active',
            panelIndex === nextClusterIndex,
          );
        });

        clusterButtons.forEach((button, buttonIndex) => {
          button.classList.toggle('page-subnav__link--active', buttonIndex === nextClusterIndex);

          if (buttonIndex === nextClusterIndex) {
            button.scrollIntoView({
              behavior: 'smooth',
              inline: 'center',
              block: 'nearest',
            });
          }
        });

        initClusterSwiper(nextClusterIndex);
        activeClusterIndex = nextClusterIndex;
      };

      clusterButtons.forEach((button, buttonIndex) => {
        button.onclick = () => {
          setActiveCluster(buttonIndex);
        };
      });

      prevEl.onclick = () => {
        swipers.get(activeClusterIndex)?.slidePrev();
      };

      nextEl.onclick = () => {
        swipers.get(activeClusterIndex)?.slideNext();
      };

      setActiveCluster(0);

      this.underSliderMounts.push({
        swipers,
        clusterButtons,
        panelEls,
        prevButton: prevEl,
        nextButton: nextEl,
      });
    });
  }

  public destroy(): void {
    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true);
      this.mainSwiper = null;
    }

    if (this.timelineSwiper) {
      this.timelineSwiper.destroy(true, true);
      this.timelineSwiper = null;
    }

    this.underSliderMounts.forEach(
      ({ swipers, clusterButtons, panelEls, prevButton, nextButton }) => {
        swipers.forEach((swiper) => {
          swiper.destroy(true, true);
        });

        clusterButtons.forEach((button) => {
          button.onclick = null;
        });
        panelEls.forEach((panelEl) => {
          panelEl.classList.remove('under-construction__swiper-panel--active');
        });

        prevButton.onclick = null;
        nextButton.onclick = null;
      },
    );
    this.underSliderMounts = [];
  }
}

registerPageInit('about-construction', () => {
  const page = new AboutConstruction();
  page.init();
});
